/* eslint-disable max-lines */
import { useMemo, useState } from 'react';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { GridAction } from '@components/atoms/datatable/grid-action-controller';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';

import { useGetCustomerBoIAM } from '@utils/api/back-office/customer-bo/use-get-customer-bo-iam';
import { useGetCustomerById } from '@utils/api/back-office/customer-bo/use-get-customer-bo';
import { useCreateCustomerBoUser } from '@utils/api/back-office/customer-bo/use-create-user-iam';
import { useResendUserActivationLinkIamBo } from '@utils/api/users/use-resend-activation-link-iam-bo';
import { useDeleteCustomerBo } from '@utils/api/back-office/customer-bo/use-delete-user-iam';

import Button from '@components/atoms/ui-kit/button.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import PageHeader from '@components/layout/page-header';
import Modal from '@components/atoms/modals/modal.tsx';
import Loader from '@components/atoms/loaders/loader';
import UpdateUserIamForm from '@components/back-office/customer/update-user-iam-form';
import { UserBo } from '@utils/api/back-office/customer-bo/use-get-iam-user';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base';
import { PencilLine, Plus, Send, Trash2 } from 'lucide-react';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';

type Row = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  roles: string[];
};

type UserFormData = {
  first_name: string;
  last_name: string;
  email: string;
  roles: string[];
};

export default function CustomerBoIam() {
  const { t } = useTranslation();
  const { customerId } = useParams<{ customerId?: string }>();
  const cid = Number(customerId) || 0;

  const [selectedUserId, setSelectedUserId] = useState<number | undefined>();
  const [selectedUser, setSelectedUser] = useState<UserBo | undefined>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [resendLinkModal, setRsendLinkModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResendingActivationLink, setIsResendingActivationLink] =
    useState(false);

  const { data: users, isLoading } = useGetCustomerBoIAM(cid);
  const { data: organization } = useGetCustomerById(cid);

  const { mutate: createUser } = useCreateCustomerBoUser(cid);
  const resendActivationLink = useResendUserActivationLinkIamBo();
  const { mutate: deleteCustomer } = useDeleteCustomerBo(
    cid,
    Number(selectedUser?.id)
  );

  const schema: yup.ObjectSchema<UserFormData> = yup
    .object({
      first_name: yup.string().required(t('first-name-is-required')),
      last_name: yup.string().required(t('last-name-is-required')),
      email: yup
        .string()
        .email(t('invalid-email'))
        .required(t('email-is-required')),
      roles: yup
        .array()
        .of(yup.string().required())
        .min(1, t('at-least-one-role-must-be-selected'))
        .required()
        .default([])
    })
    .required();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<UserFormData>({
    resolver: yupResolver(schema),
    defaultValues: { first_name: '', last_name: '', email: '', roles: [] }
  });

  const onSubmit = (data: UserFormData) => {
    setIsSubmitting(true);
    createUser(data, {
      onSuccess: () => {
        toast.success(t('user-created-successfully'));
        setIsCreateModalOpen(false);
        reset();
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const d = (error as AxiosError<BaseError>)?.response?.data as any;
        const msg =
          d?.error_description ||
          d?.message ||
          d?.error ||
          (Array.isArray(d?.detail)
            ? d.detail
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .map((e: any) => `${e.loc?.[1] ?? 'field'}: ${e.msg}`)
                .join('\n')
            : t('an-error-occurred-while-creating-the-customer'));
        toast.error(String(msg));
      },
      onSettled: () => setIsSubmitting(false)
    });
  };

  const handleDeleteUser = async () => {
    if (!selectedUserId) return;
    setIsDeleting(true);
    await new Promise(r => setTimeout(r, 300));
    deleteCustomer(undefined, {
      onSuccess: () => {
        toast.success(t('customer-deleted-successfully'));
        setDeleteModalOpen(false);
        setIsDeleting(false);
      },
      onError: (error: AxiosError<BaseError>) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const d = error.response?.data as any;
        const msg =
          d?.error_description ||
          d?.detail ||
          d?.error ||
          d?.message ||
          t('an-error-occurred-while-deleting-the-customer');
        toast.error(String(msg));
        setDeleteModalOpen(false);
        setIsDeleting(false);
      }
    });
  };

  // Do NOT open edit on row click; just remember what was clicked (actions/menu open modals)
  const handleCellClick = (row: Row) => {
    setSelectedUserId(row.id);
    setSelectedUser(row as unknown as UserBo);
  };

  const handleResendActivation = async () => {
    if (!selectedUserId) return;
    setIsResendingActivationLink(true);
    await new Promise(r => setTimeout(r, 300));
    resendActivationLink.mutate(
      { customerId: cid, userId: selectedUserId },
      {
        onSuccess: () => {
          toast.success(t('activation-link-resent-successfully'));
          setIsResendingActivationLink(false);
        },
        onError: (error: AxiosError<BaseError>) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const d = error.response?.data as any;
          const msg =
            d?.error_description ||
            d?.message ||
            (Array.isArray(d?.detail)
              ? d.detail
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  .map((e: any) => `${e.loc?.[1] ?? 'field'}: ${e.msg}`)
                  .join('\n')
              : t('failed-to-resent-activation-link'));
          toast.error(String(msg));
          setIsResendingActivationLink(false);
        },
        onSettled: () => setRsendLinkModal(false)
      }
    );
  };

  /* ---------- Columns ---------- */
  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        header: t('id'),
        field: 'id',
        maxWidth: 90,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number'
      },
      {
        header: t('first-name'),
        field: 'first_name',
        minWidth: 220,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text'
      },
      {
        header: t('last-name'),
        field: 'last_name',
        minWidth: 220,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text'
      },
      {
        header: t('email'),
        field: 'email',
        minWidth: 260,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        enableRowCopyOnClick: true
      }
    ],
    [t]
  );

  const rows = useMemo<Row[]>(() => {
    const r = users?.result ?? [];
    return r.map(u => ({
      id: u.id,
      email: u.email || '',
      first_name: u.first_name || '',
      last_name: u.last_name || '',
      roles: u.roles || []
    }));
  }, [users]);

  /* ---------- Actions (left-click buttons) ---------- */
  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <PencilLine className="h-4 w-4" />,
        intent: 'primary',
        run: row => {
          setSelectedUserId(row.id);
          setSelectedUser(row as unknown as UserBo);
          setIsEditModalOpen(true);
        }
      },
      {
        id: 'resend_link',
        label: t('send-reactivation-link'),
        icon: <Send className="h-4 w-4" />,
        run: row => {
          setSelectedUserId(row.id);
          setSelectedUser(row as unknown as UserBo);
          setRsendLinkModal(true);
        }
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        run: row => {
          setSelectedUserId(row.id);
          setSelectedUser(row as unknown as UserBo);
          setDeleteModalOpen(true);
        }
      }
    ],
    [t]
  );

  /* ---------- Right-click context menu (mirrors actions) ---------- */
  const menuOptions = useMemo(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <PencilLine className="h-4 w-4" />
      },
      {
        id: 'resend_link',
        label: t('send-reactivation-link'),
        icon: <Send className="h-4 w-4" />
      },
      { id: 'delete', label: t('delete'), icon: <Trash2 className="h-4 w-4" /> }
    ],
    [t]
  );

  const handleSetMenuOptions = (row: Row) => {
    setSelectedUserId(row.id);
    setSelectedUser(row as unknown as UserBo);
  };

  const handleMenuOptionClick = (optionId: string) => {
    if (!selectedUserId) return;
    if (optionId === 'edit') setIsEditModalOpen(true);
    else if (optionId === 'resend_link') setRsendLinkModal(true);
    else if (optionId === 'delete') setDeleteModalOpen(true);
  };

  const FORM_ID = 'create-iam-user-form';

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {isLoading ? (
        <div className="h-screen w-full flex justify-center items-center -mt-24">
          <Loader height={10} width={3} />
        </div>
      ) : (
        <div className={cn('min-w-full flex-1 min-h-0 flex flex-col gap-y-4')}>
          <PageHeader
            title={t('identity-and-access-management')}
            caption={t(
              'list-of-all-the-users-including-their-name-contact-and-actions'
            )}
            contentClassName="px-2"
          >
            <Button
              icon={<Plus width={14} />}
              onClick={() => setIsCreateModalOpen(true)}
              label={t('new-user')}
              variant="primary"
              size="small"
            />
          </PageHeader>

          <DataGridV2<Row>
            data={rows}
            columns={columns}
            actions={actions}
            enableRowSelection={false}
            enablePagination
            manualPagination
            enableSorting
            manualSorting
            enableFiltering
            manualFiltering
            totalItems={users?.total_results ?? rows.length}
            pageSize={25}
            pageSizeOptions={[25, 50, 75, 100]}
            showColumnFilters={false}
            filterMode="popover"
            fill
            dense
            striped
            grid={false}
            loading={isLoading}
            onCellClick={handleCellClick}
            menuOptions={menuOptions}
            setMenuOptions={handleSetMenuOptions}
            onMenuOptionClick={handleMenuOptionClick}
            getRowId={row => String(row.id)}
          />
        </div>
      )}

      {/* Delete confirm */}
      {selectedUserId && (
        <ConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleDeleteUser}
          size="small"
          title={t('delete-confirmation')}
          caption={t('are-you-sure-you-want-to-delete-this-user')}
          confirmLabel={isDeleting ? t('deleting') : t('delete')}
          cancelLabel={t('cancel')}
          confirmDisabled={isDeleting}
          destructive
          autoFocusConfirm
        />
      )}

      {/* Resend activation link */}
      <ConfirmationModal
        isOpen={resendLinkModal}
        onClose={() => setRsendLinkModal(false)}
        onConfirm={handleResendActivation}
        size="small"
        title={t('resend-activation-link')}
        caption={t(
          'by-clicking-confirm-you-will-resend-activation-link-to-the-users-email'
        )}
        confirmLabel={isResendingActivationLink ? t('resending') : t('resend')}
        cancelLabel={t('cancel')}
        confirmDisabled={isResendingActivationLink}
        destructive={false}
        autoFocusConfirm
      />

      {/* Create user */}
      {isCreateModalOpen && (
        <Modal
          isOpen={isCreateModalOpen}
          onClose={() => !isSubmitting && setIsCreateModalOpen(false)}
          size="small"
          title={t('create-user')}
          footerActions={[
            <Button
              key="cancel"
              type="button"
              onClick={() => {
                reset();
                setIsCreateModalOpen(false);
              }}
              label={t('cancel')}
              size="medium"
              variant="secondary"
              disabled={isSubmitting}
            />,
            <Button
              key="create"
              type="submit"
              form={FORM_ID}
              label={isSubmitting ? t('creating') : t('create')}
              variant="primary"
              size="small"
              disabled={isSubmitting}
            />
          ]}
        >
          <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
            <button type="submit" className="hidden" aria-hidden="true" />
            <div className="space-y-4">
              <Input
                id="first_name"
                type="text"
                label={t('first-name')}
                autoComplete="first-name"
                {...register('first_name')}
                error={errors.first_name?.message}
              />
              <Input
                id="last_name"
                label={t('last-name')}
                type="text"
                autoComplete="last-name"
                {...register('last_name')}
                error={errors.last_name?.message}
              />
              <Input
                id="email"
                label={t('email')}
                type="email"
                autoComplete="email"
                {...register('email')}
                error={errors.email?.message}
              />
              <p className="mt-2 font-semibold text-sm">{t('role')}:</p>
              <div className="grid gap-2 w-1/2">
                <Checkbox
                  id="admin"
                  label={t('admin')}
                  register={register('roles')}
                  value="admin"
                  variant="primary"
                />
                <Checkbox
                  id="contributor"
                  label={t('contributor')}
                  register={register('roles')}
                  value="contributor"
                  variant="primary"
                />
                {organization?.result.is_reseller && (
                  <Checkbox
                    id="reseller"
                    label={t('reseller')}
                    register={register('roles')}
                    value="reseller"
                    variant="primary"
                  />
                )}
                {errors.roles && (
                  <div className="sm:col-span-6">
                    <p className="text-red-500 text-sm">
                      {errors.roles.message}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit user */}
      {isEditModalOpen && selectedUser && (
        <UpdateUserIamForm
          selectedUser={selectedUser}
          isEditModalOpen={isEditModalOpen}
          setIsEditModalOpen={setIsEditModalOpen}
        />
      )}
    </div>
  );
}
